// StepFileView.fixed.tsx
import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Tooltip } from "@mui/material";
import "./StepFileView.css";

const style = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    height: "80%",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 0
};

declare global {
    interface Window {
        Autodesk: any;
    }
}

// === keep your fetchToken for local testing only ===
// In production, move this to a server endpoint and call that.
const fetchToken = async (): Promise<string> => {
    const res = await fetch(
        "https://developer.api.autodesk.com/authentication/v2/token",
        {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
                client_id: process.env.REACT_APP_AUTO_DESK_CLIENT_ID || "",
                client_secret:
                    process.env.REACT_APP_AUTO_DESK_CLIENT_SECRET || "",
                grant_type: "client_credentials",
                scope: "data:read data:write bucket:read bucket:create"
            })
        }
    );

    if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Token fetch failed ${res.status}: ${txt}`);
    }

    const data = await res.json();
    if (!data.access_token) {
        throw new Error(
            "No access_token in token response: " + JSON.stringify(data)
        );
    }
    return data.access_token;
};

// Load viewer script & css (idempotent)
async function ensureViewerResources() {
    const CSS =
        "https://developer.api.autodesk.com/modelderivative/v2/viewers/style.min.css?v=7.*";
    const JS =
        "https://developer.api.autodesk.com/modelderivative/v2/viewers/viewer3D.min.js?v=7.*";

    // load CSS once
    if (
        !Array.from(document.head.querySelectorAll("link")).some((l) =>
            (l as HTMLLinkElement).href.includes("style.min.css")
        )
    ) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = CSS;
        document.head.appendChild(link);
        console.log("[Forge] added CSS");
    }

    // if Autodesk already present, done
    if ((window as any).Autodesk && (window as any).Autodesk.Viewing) {
        return;
    }

    // if script already present, wait for global to become available
    const existing = Array.from(document.scripts).find((s) =>
        (s.src || "").includes("viewer3D.min.js")
    );
    if (existing) {
        await waitForAutodesk();
        return;
    }

    // add script
    await new Promise<void>((resolve, reject) => {
        const s = document.createElement("script");
        s.src = JS;
        s.async = true;
        s.onload = async () => {
            console.log("[Forge] viewer script loaded");
            try {
                await waitForAutodesk();
                resolve();
            } catch (e) {
                reject(e);
            }
        };
        s.onerror = (e) =>
            reject(new Error("Failed to load Forge viewer script: " + e));
        document.body.appendChild(s);
    });
}

function waitForAutodesk(): Promise<void> {
    return new Promise((resolve, reject) => {
        let attempts = 0;
        const check = () => {
            attempts++;
            if ((window as any).Autodesk && (window as any).Autodesk.Viewing)
                return resolve();
            if (attempts > 100)
                return reject(
                    new Error("Autodesk global not available after waiting")
                );
            setTimeout(check, 150);
        };
        check();
    });
}

// Normalize urn: ensure we produce 'urn:BASE64'
function toUrnBase64(input: string) {
    if (!input) return "";
    const trimmed = input.trim();

    // if already starts with 'urn:' and looks base64 after that, return as-is OR re-format
    if (trimmed.startsWith("urn:")) {
        const payload = trimmed.slice(4);
        // if payload looks like base64, return `urn:${payload}`
        if (/^[A-Za-z0-9+/=]+$/.test(payload)) return `urn:${payload}`;
        // if payload already looks non-base64, attempt to base64 the payload
        try {
            return `urn:${btoa(payload)}`;
        } catch (e) {
            return trimmed;
        }
    }

    // If looks like raw base64 already
    if (/^[A-Za-z0-9+/=]+$/.test(trimmed)) return `urn:${trimmed}`;

    // else attempt to base64 the raw id
    try {
        return `urn:${btoa(trimmed)}`;
    } catch (e) {
        // last resort: return trimmed (may fail later)
        return trimmed;
    }
}

interface Props {
    urn: string;
    fileName?: string;
}

const StepFileView: React.FC<Props> = ({ urn, fileName }) => {
    const viewerDiv = useRef<HTMLDivElement | null>(null);
    const viewerInstance = useRef<any>(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const MODEL_URN_BASE64 = toUrnBase64(urn);

    useEffect(() => {
        if (!open || !MODEL_URN_BASE64) return;

        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                console.log("[Forge] ensure resources...");
                await ensureViewerResources();

                console.log("[Forge] fetching token...");
                const token = await fetchToken();

                if (cancelled) return;
                if (!viewerDiv.current) {
                    console.warn("[Forge] viewerDiv not available");
                    setLoading(false);
                    return;
                }

                const options = {
                    env: "AutodeskProduction",
                    getAccessToken: (
                        cb: (t: string, expires: number) => void
                    ) => cb(token, 3600)
                };

                console.log(
                    "[Forge] Initializer start",
                    options,
                    MODEL_URN_BASE64
                );

                (window as any).Autodesk.Viewing.Initializer(
                    options,
                    async () => {
                        try {
                            // cleanup any previous
                            if (viewerInstance.current) {
                                try {
                                    viewerInstance.current.finish();
                                } catch (e) {
                                    console.warn(
                                        "[Forge] ignore finish error",
                                        e
                                    );
                                }
                                viewerInstance.current = null;
                            }

                            const viewer = new (
                                window as any
                            ).Autodesk.Viewing.GuiViewer3D(viewerDiv.current, {
                                extensions: [
                                    "Autodesk.Measure",
                                    "Autodesk.Section"
                                ]
                            });

                            viewer.start();
                            viewerInstance.current = viewer;

                            // single, correct Document.load call:
                            (window as any).Autodesk.Viewing.Document.load(
                                MODEL_URN_BASE64,
                                (doc: any) => {
                                    try {
                                        const defaultModel = doc
                                            .getRoot()
                                            .getDefaultGeometry();
                                        viewer
                                            .loadDocumentNode(doc, defaultModel)
                                            .then(() => {
                                                console.log(
                                                    "[Forge] model loaded"
                                                );
                                                setLoading(false);
                                            });
                                    } catch (err) {
                                        console.error(
                                            "[Forge] load geometry error",
                                            err
                                        );
                                        setLoading(false);
                                    }
                                },
                                (err: any) => {
                                    console.error(
                                        "[Forge] Document.load error:",
                                        err
                                    );
                                    setLoading(false);
                                }
                            );
                        } catch (err) {
                            console.error("[Forge] viewer init error", err);
                            setLoading(false);
                        }
                    }
                );
            } catch (err) {
                console.error("[Forge] overall error:", err);
                setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            setLoading(false);
            if (viewerInstance.current) {
                try {
                    viewerInstance.current.finish();
                } catch (e) {
                    /* ignore */
                }
                viewerInstance.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, MODEL_URN_BASE64]);

    return (
        <>
            <IconButton
                onClick={() => setOpen(true)}
                color="primary"
                sx={{ paddingLeft: "0px" }}
            >
                <Tooltip title="view">
                    <VisibilityIcon fontSize="small" />
                </Tooltip>
            </IconButton>

            <Modal open={open} onClose={() => setOpen(false)}>
                <Box sx={style}>
                    <Box
                        ref={viewerDiv}
                        sx={{ width: "100%", height: "100%" }}
                    />
                    <Button
                        variant="contained"
                        onClick={() => setOpen(false)}
                        sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            zIndex: 9999
                        }}
                    >
                        Close
                    </Button>

                    {loading && (
                        <div
                            className="forgeLoaderOverlay"
                            role="status"
                            aria-live="polite"
                        >
                            <div className="forgeSpinner" />
                            <div className="forgeLoaderText">
                                Loading viewer…
                            </div>
                        </div>
                    )}
                </Box>
            </Modal>
        </>
    );
};

export default StepFileView;
